/* eslint-disable no-underscore-dangle */
import React, {
  useEffect, useRef, useState,
} from 'react';
import {
  Button, Card, Form, Modal,
} from 'react-bootstrap';
import Error from '../../../Components/Error';
import PaymentsService from '../../../Services/PaymentsService';
import counterpartyIdOf from './counterpartyIdOf';
import PartnersService from '../../../Services/PartnersService';
import OperatorService from '../../../Services/OperatorService';
import { useAccountsList } from '../../../Hooks/useAccountsCache';
import PaymentAllocationRows, {
  newAllocationRow,
  validateAllocationRows,
  rowsToLinesPayload,
} from './PaymentAllocationRows';
import occurredAtIso from '../../../Utils/occurredAt';

// Local YYYY-MM-DD for today. Local zone (not UTC) so the date input
// pre-fills with what the admin sees on the wall calendar; we convert
// to a full ISO string on submit.
function todayYmd() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// Counterparty kinds the admin can target. `system` is reserved for
// unmatched webhook payments; admins reconcile those via the
// LinkToBookingModal flow rather than typing a new system payment.
const COUNTERPARTY_KINDS = ['booking', 'partner', 'operator'];

const CURRENCIES = ['EUR', 'HUF'];

function RecordPaymentModal({
  show, onHide, onRecorded,
}) {
  // Stable per-row ids so React keys survive cross-row state changes
  // even when no booking has been picked yet.
  const rowIdCounter = useRef(0);
  const newRowId = () => {
    rowIdCounter.current += 1;
    return `rpm-row-${rowIdCounter.current}`;
  };

  // Top-level state. Plain useState to match the rest of the codebase
  // (no react-hook-form / formik).
  const [direction, setDirection] = useState('in');
  const [accountId, setAccountId] = useState('');
  const [counterpartyKind, setCounterpartyKind] = useState('booking');
  // Umbrella id for partner / operator counterparty (the picker at the
  // top). For kind='booking' counterparty.id is always rows[0].matchedBooking._id.
  const [counterpartyId, setCounterpartyId] = useState('');

  // Accounts list — shared cache. We only let the admin pick from
  // ACTIVE accounts here; inactive accounts are kept for historical
  // payments display only.
  const { accounts: allAccounts } = useAccountsList();
  const activeAccounts = allAccounts.filter((a) => a.is_active);

  // Partner / operator option lists, cached after first load.
  const [partners, setPartners] = useState([]);
  const [operators, setOperators] = useState([]);
  const [loadingPartners, setLoadingPartners] = useState(false);
  const [loadingOperators, setLoadingOperators] = useState(false);

  // Money
  const [grossAmount, setGrossAmount] = useState('');
  const [fee, setFee] = useState('0');
  const [currency, setCurrency] = useState('EUR');
  const [occurredAt, setOccurredAt] = useState(todayYmd());

  // Reference
  const [bankReference, setBankReference] = useState('');
  const [notes, setNotes] = useState('');

  // Allocation rows — owned by this component, driven by the shared
  // PaymentAllocationRows picker. Default to a single empty row; the
  // operator adds more via "Add another allocation" inside the picker.
  const [rows, setRows] = useState(() => [newAllocationRow('rpm-row-init')]);

  // Submit lifecycle
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [validationError, setValidationError] = useState('');

  // Refs to guard the lazy loaders.
  const partnersRequestedRef = useRef(false);
  const operatorsRequestedRef = useRef(false);

  // Reset all transient state whenever the modal (re-)opens.
  useEffect(() => {
    if (!show) return;
    setDirection('in');
    setAccountId('');
    setCounterpartyKind('booking');
    setCounterpartyId('');
    setGrossAmount('');
    setFee('0');
    setCurrency('EUR');
    setOccurredAt(todayYmd());
    setBankReference('');
    setNotes('');
    rowIdCounter.current = 0;
    setRows([newAllocationRow(newRowId())]);
    setError('');
    setValidationError('');
    setSubmitting(false);
  }, [show]);

  // Lazy-load the partner list the first time the partner picker is
  // selected. Cached in component state so re-selecting doesn't refetch.
  useEffect(() => {
    if (!show) return undefined;
    if (counterpartyKind !== 'partner') return undefined;
    if (partnersRequestedRef.current) return undefined;
    partnersRequestedRef.current = true;
    let cancelled = false;
    (async () => {
      setLoadingPartners(true);
      try {
        const response = await PartnersService.fetchPartners();
        if (cancelled) return;
        const list = response?.data?.data || [];
        setPartners(Array.isArray(list) ? list : []);
      } catch (err) {
        if (cancelled) return;
        setError(err.response?.data?.message || err.message || 'Failed to load partners');
        partnersRequestedRef.current = false; // allow a retry
      } finally {
        if (!cancelled) setLoadingPartners(false);
      }
    })();
    return () => { cancelled = true; };
  }, [show, counterpartyKind]);

  // Same lazy-load pattern for operators. Operators are needed both for
  // the top-level counterparty picker AND for the per-row operator
  // dropdown the shared component renders when sideMode='operator'.
  useEffect(() => {
    if (!show) return undefined;
    if (counterpartyKind !== 'operator') return undefined;
    if (operatorsRequestedRef.current) return undefined;
    operatorsRequestedRef.current = true;
    let cancelled = false;
    (async () => {
      setLoadingOperators(true);
      try {
        const response = await OperatorService.fetchOperators();
        if (cancelled) return;
        const list = response?.data?.data || [];
        setOperators(Array.isArray(list) ? list : []);
      } catch (err) {
        if (cancelled) return;
        setError(err.response?.data?.message || err.message || 'Failed to load operators');
        operatorsRequestedRef.current = false; // allow a retry
      } finally {
        if (!cancelled) setLoadingOperators(false);
      }
    })();
    return () => { cancelled = true; };
  }, [show, counterpartyKind]);

  // When the kind changes, clear the resolved counterparty AND reset
  // the rows. A booking-kind row carries a customer-side booking; an
  // operator-kind row needs the per-row operator dropdown. Letting them
  // leak across kinds would result in invalid payloads.
  const handleKindChange = (nextKind) => {
    setCounterpartyKind(nextKind);
    setCounterpartyId('');
    rowIdCounter.current = 0;
    setRows([newAllocationRow(newRowId())]);
    setValidationError('');
  };

  // Switching direction to 'out' is most often an operator payout, so
  // default the kind there if the user hasn't already picked something
  // intentional. Don't FORCE it (refunds to partners are valid) — just
  // nudge the default.
  const handleDirectionChange = (nextDirection) => {
    setDirection(nextDirection);
    if (nextDirection === 'out' && counterpartyKind === 'booking') {
      handleKindChange('operator');
    }
  };

  // ---- Validation ------------------------------------------------------

  const grossNum = Number(grossAmount);
  const sideMode = counterpartyKind === 'operator' ? 'operator' : 'customer';
  const rowsValidation = validateAllocationRows(rows, grossNum, { sideMode });

  // For partner/operator the operator must pick the umbrella id at the
  // top; for booking the rows[0].matchedBooking._id is the effective
  // counterparty.id (nominated-primary convention).
  const effectiveCounterpartyId = counterpartyKind === 'booking'
    ? (rows[0]?.matchedBooking?._id || '')
    : counterpartyId;
  const counterpartyValid = !!effectiveCounterpartyId;

  const basicValid = grossAmount !== ''
    && Number.isFinite(grossNum) && grossNum > 0
    && !!currency
    && !!occurredAt
    && counterpartyValid
    && !!accountId;

  const canSubmit = basicValid && rowsValidation.canSubmit && !submitting;

  // ---- Submit ----------------------------------------------------------

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setValidationError('');
    setError('');

    if (!Number.isFinite(grossNum) || grossNum <= 0) {
      setValidationError('Amount must be greater than 0');
      return;
    }
    if (!currency) {
      setValidationError('Currency is required');
      return;
    }
    if (!occurredAt) {
      setValidationError('Date is required');
      return;
    }
    if (!effectiveCounterpartyId) {
      setValidationError('Pick a counterparty');
      return;
    }
    if (!accountId) {
      setValidationError('Pick an account');
      return;
    }
    if (!rowsValidation.canSubmit) {
      if (!rowsValidation.allBookingsResolved) {
        setValidationError('Every allocation row needs a resolved booking reference');
      } else if (!rowsValidation.allAmountsPositive) {
        setValidationError('Every allocation row needs a positive amount');
      } else if (!rowsValidation.sumMatches) {
        setValidationError('Allocations must sum to the gross amount');
      } else if (!rowsValidation.distinctRows) {
        setValidationError('Two rows target the same booking and order line — combine them');
      } else if (!rowsValidation.allOperatorsAssigned) {
        setValidationError('Every operator-side row needs an operator');
      } else {
        setValidationError('Fix the allocation rows before submitting');
      }
      return;
    }

    const lines = rowsToLinesPayload(rows, { sideMode });

    setSubmitting(true);
    try {
      const payload = {
        direction,
        account_id: accountId,
        gross_amount: grossNum,
        fee: parseFloat(fee) || 0,
        currency,
        occurred_at: occurredAtIso(occurredAt),
        counterparty: { kind: counterpartyKind, id: effectiveCounterpartyId },
        external_refs: bankReference ? { bank_reference: bankReference } : undefined,
        notes: notes || undefined,
        lines,
      };
      const created = await PaymentsService.recordPayment(payload);
      if (onRecorded) onRecorded(created);
      if (onHide) onHide();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (submitting) return;
    if (onHide) onHide();
  };

  // ---- Render ----------------------------------------------------------

  // Umbrella picker for partner / operator counterparty. For booking
  // kind there is no umbrella — the picker lives inside the row UI.
  const renderUmbrellaPicker = () => {
    if (counterpartyKind === 'partner') {
      return (
        <Form.Group controlId="rpm_counterparty_partner">
          <Form.Label>
            Partner
            {' '}
            *
          </Form.Label>
          <Form.Select
            value={counterpartyId}
            onChange={(e) => setCounterpartyId(e.target.value)}
            disabled={loadingPartners}
          >
            <option value="">
              {loadingPartners ? 'Loading partners…' : 'Select partner…'}
            </option>
            {partners.map((p) => {
              const id = counterpartyIdOf(p);
              return (
                <option key={id} value={id}>
                  {p.name || id}
                </option>
              );
            })}
          </Form.Select>
        </Form.Group>
      );
    }
    if (counterpartyKind === 'operator') {
      return (
        <Form.Group controlId="rpm_counterparty_operator">
          <Form.Label>
            Operator
            {' '}
            *
          </Form.Label>
          <Form.Select
            value={counterpartyId}
            onChange={(e) => setCounterpartyId(e.target.value)}
            disabled={loadingOperators}
          >
            <option value="">
              {loadingOperators ? 'Loading operators…' : 'Select operator…'}
            </option>
            {operators.map((o) => {
              const id = counterpartyIdOf(o);
              return (
                <option key={id} value={id}>
                  {o.name || id}
                </option>
              );
            })}
          </Form.Select>
          <Form.Text className="text-muted">
            For multi-operator bookings (e.g. driver + guide), each row below
            can also carry its own operator.
          </Form.Text>
        </Form.Group>
      );
    }
    return null;
  };

  return (
    <Modal show={show} onHide={handleCancel} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa fa-plus me-2" />
          Record Payment
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Error error={error} setError={setError} />
        {validationError && (
          <div className="alert alert-warning py-2 mb-3 small" role="alert">
            {validationError}
          </div>
        )}

        <Form onSubmit={handleSubmit}>
          {/* Direction + method + counterparty kind */}
          <Card className="mb-3">
            <Card.Body>
              <Card.Title as="h6" className="mb-3">What is this payment?</Card.Title>

              <Form.Group className="mb-3">
                <Form.Label className="d-block">Direction *</Form.Label>
                <Form.Check
                  inline
                  type="radio"
                  id="rpm_dir_in"
                  name="rpm_direction"
                  label="Incoming (we received money)"
                  checked={direction === 'in'}
                  onChange={() => handleDirectionChange('in')}
                />
                <Form.Check
                  inline
                  type="radio"
                  id="rpm_dir_out"
                  name="rpm_direction"
                  label="Outgoing (we paid)"
                  checked={direction === 'out'}
                  onChange={() => handleDirectionChange('out')}
                />
              </Form.Group>

              <div className="row g-2">
                <Form.Group className="col-md-6" controlId="rpm_account">
                  <Form.Label>
                    Account
                    {' '}
                    *
                  </Form.Label>
                  <Form.Select
                    value={accountId}
                    onChange={(e) => setAccountId(e.target.value)}
                  >
                    <option value="">Select account…</option>
                    {activeAccounts.map((a) => (
                      <option key={a._id} value={a._id}>
                        {a.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Where the money moved through (Stripe, K&amp;H, Cash HUF, …).
                  </Form.Text>
                </Form.Group>

                <Form.Group className="col-md-6" controlId="rpm_kind">
                  <Form.Label>
                    Counterparty
                    {' '}
                    *
                  </Form.Label>
                  <Form.Select
                    value={counterpartyKind}
                    onChange={(e) => handleKindChange(e.target.value)}
                  >
                    {COUNTERPARTY_KINDS.map((k) => (
                      <option key={k} value={k}>{k}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>

              {(counterpartyKind === 'partner' || counterpartyKind === 'operator') && (
                <div className="mt-3">
                  {renderUmbrellaPicker()}
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Money */}
          <Card className="mb-3">
            <Card.Body>
              <Card.Title as="h6" className="mb-3">Amount</Card.Title>
              <div className="row g-2">
                <Form.Group className="col-md-4" controlId="rpm_amount">
                  <Form.Label>
                    Amount
                    {' '}
                    *
                  </Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={grossAmount}
                    onChange={(e) => setGrossAmount(e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    In major units (e.g. 99.99 EUR, not cents).
                  </Form.Text>
                </Form.Group>

                <Form.Group className="col-md-2" controlId="rpm_fee">
                  <Form.Label>Fee</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    value={fee}
                    onChange={(e) => setFee(e.target.value)}
                  />
                </Form.Group>

                <Form.Group className="col-md-3" controlId="rpm_currency">
                  <Form.Label>
                    Currency
                    {' '}
                    *
                  </Form.Label>
                  <Form.Select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                  >
                    {CURRENCIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="col-md-3" controlId="rpm_occurred_at">
                  <Form.Label>
                    Occurred on
                    {' '}
                    *
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={occurredAt}
                    onChange={(e) => setOccurredAt(e.target.value)}
                  />
                </Form.Group>
              </div>
            </Card.Body>
          </Card>

          {/* Reference */}
          <Card className="mb-3">
            <Card.Body>
              <Card.Title as="h6" className="mb-3">Reference</Card.Title>
              <Form.Group className="mb-3" controlId="rpm_bank_reference">
                <Form.Label>Bank reference</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. statement line ID"
                  value={bankReference}
                  onChange={(e) => setBankReference(e.target.value)}
                />
              </Form.Group>
              <Form.Group controlId="rpm_notes">
                <Form.Label>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </Form.Group>
            </Card.Body>
          </Card>

          {/* Allocations — shared component drives both single AND
              multi-row via "Add another allocation". Default is one row;
              operator adds more if needed. */}
          <Card className="mb-3">
            <Card.Body>
              <Card.Title as="h6" className="mb-3">Allocation</Card.Title>
              <PaymentAllocationRows
                rows={rows}
                onChange={setRows}
                expectedTotal={grossNum}
                currency={currency}
                sideMode={sideMode}
                operators={operators}
                loadingOperators={loadingOperators}
                disabled={submitting}
                testIdPrefix="rpm"
                // Scope the per-row booking pickers to the counterparty
                // so the operator can't accidentally attach a booking
                // belonging to a different operator/partner (Layer-3).
                counterpartyKind={counterpartyKind}
                counterpartyId={effectiveCounterpartyId}
                // Customer-side only: suggest unpaid bookings matching the
                // amount/date being recorded (operator/partner payouts don't
                // reconcile against unpaid customer bookings).
                matchSignals={sideMode === 'customer' ? {
                  amount: grossNum,
                  currency,
                  date: occurredAt,
                } : null}
              />
            </Card.Body>
          </Card>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={!canSubmit}>
          {submitting ? 'Recording…' : 'Record Payment'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default RecordPaymentModal;
